import React, { createContext, useCallback, useContext, useState, useRef, useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X, WifiOff } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  exiting?: boolean;
  action?: ToastAction;
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} });

export const useToast = () => useContext(ToastContext);

const ICONS: Record<ToastType, React.FC<{ className?: string }>> = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
};

const COLORS: Record<ToastType, string> = {
  success: 'text-emerald-500',
  error: 'text-red-500',
  info: 'text-blue-500',
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [offline, setOffline] = useState(!navigator.onLine);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  // Offline detection
  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  const removeToast = useCallback((id: number) => {
    // Clear any pending auto-dismiss timer
    const timer = timersRef.current.get(id);
    if (timer) { clearTimeout(timer); timersRef.current.delete(id); }
    // Start exit animation
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    // Remove after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 200);
  }, []);

  const toast = useCallback((message: string, type: ToastType = 'info', action?: ToastAction) => {
    const id = ++idRef.current;
    setToasts(prev => [...prev, { id, message, type, action }]);
    // Give more time when there's an undo action
    const delay = action ? 6000 : 4000;
    const timer = setTimeout(() => removeToast(id), delay);
    timersRef.current.set(id, timer);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Offline banner */}
      {offline && (
        <div className="fixed top-0 left-0 right-0 z-[999] flex items-center justify-center gap-2 px-4 py-2 bg-[var(--status-error-bg)] border-b border-[var(--error)] text-sm text-[var(--error)] font-medium">
          <WifiOff className="w-4 h-4" />
          You're offline — changes won't be saved
        </div>
      )}
      {/* Toast container */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[999] flex flex-col gap-2 pointer-events-none" style={{ maxWidth: '360px' }}>
          {toasts.map(t => {
            const Icon = ICONS[t.type];
            return (
              <div
                key={t.id}
                className="pointer-events-auto flex items-center gap-2.5 px-4 py-3 rounded-xl border border-[var(--border-primary)] bg-[var(--surface-elevated)] text-sm text-[var(--content-primary)]"
                style={{
                  boxShadow: 'var(--shadow-lg)',
                  animation: t.exiting
                    ? 'toastOut 200ms ease forwards'
                    : 'toastIn 250ms cubic-bezier(0.16,1,0.3,1) both',
                }}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${COLORS[t.type]}`} />
                <span className="flex-1 leading-snug">{t.message}</span>
                {t.action && (
                  <button
                    onClick={() => { t.action!.onClick(); removeToast(t.id); }}
                    className="flex-shrink-0 text-[13px] font-semibold text-[var(--accent-primary)] hover:text-[var(--accent-hover)] transition-colors px-1"
                  >
                    {t.action.label}
                  </button>
                )}
                <button
                  onClick={() => removeToast(t.id)}
                  className="flex-shrink-0 p-0.5 rounded text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] transition-colors"
                  aria-label="Dismiss notification"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </ToastContext.Provider>
  );
};
